import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocationStore } from "@/lib/store";
import type { GbpProduct, Location } from "@shared/schema";
import {
  Sparkles, Plus, Trash2, ExternalLink, Copy, ChevronDown, ChevronUp,
  Package, DollarSign, Link2, FileText, Target, Star, Loader2,
  Search, PenTool, CheckCircle2, AlertTriangle, Edit3, Save, X,
  Image as ImageIcon, Upload, Send, Globe, Download, ClipboardCopy, Bot,
  UploadCloud,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { RtdOptOut } from "@/components/rtd-opt-out";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const DD = {
  bg: "#0a0a0a",
  surface: "rgba(255,255,255,0.025)",
  surfaceSolid: "#111111",
  border: "rgba(255,255,255,0.06)",
  borderSolid: "#1a1a1a",
  text: "#f1f5f9",
  textSecondary: "rgba(255,255,255,0.5)",
  textTertiary: "rgba(255,255,255,0.22)",
  accent: "#f97316",
  accentHover: "#ea580c",
  accentBg: "rgba(249,115,22,0.10)",
  accentBorder: "rgba(249,115,22,0.25)",
  green: "#22c55e",
  blue: "#3b82f6",
  purple: "#8b5cf6",
  warning: "#f59e0b",
  danger: "#ef4444",
};

const PRIORITY_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  tier1: { label: "Tier 1", color: DD.accent, bg: DD.accentBg },
  tier2: { label: "Tier 2", color: DD.blue, bg: "rgba(59,130,246,0.10)" },
  tier3: { label: "Tier 3", color: DD.purple, bg: "rgba(139,92,246,0.10)" },
};

const PRICE_STRATEGIES: Record<string, string> = {
  low_entry: "Low Entry + Upsell",
  starting_at: "Starting At",
  package: "Package Price",
  comparison: "Comparison Anchor",
  free_value: "Free + Value",
  bundled: "Bundled Savings",
};

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; tip: string }> = {
  draft: {
    label: "Optimized — Ready",
    color: DD.accent,
    bg: "rgba(249,115,22,0.10)",
    tip: "Optimized and ready to push to your GBP product feed.",
  },
  published: {
    label: "Live & Indexing",
    color: DD.green,
    bg: "rgba(34,197,94,0.10)",
    tip: "Published to GBP and being crawled by Google — every visit is a ranking signal.",
  },
};

// Outcome language helpers — replace engineering metrics (chars/score/etc.)
// with positive signals that read as wins, with the raw number kept on hover
// so power users still see the underlying data.
function depthLabel(chars: number) {
  if (chars >= 800) return { label: "Authoritative", color: DD.green, tip: `${chars} chars — comprehensive depth, top-tier signal of topical authority to Google.` };
  if (chars >= 500) return { label: "Strong", color: DD.green, tip: `${chars} chars — solid topical depth that Google rewards.` };
  if (chars >= 300) return { label: "Solid", color: DD.green, tip: `${chars} chars — foundation in place, room to compound the signal further.` };
  return { label: "Building", color: DD.accent, tip: `${chars} chars — already sending signal; pushing past 500 amplifies it.` };
}

function signalLabel(score: number) {
  if (score >= 85) return { label: "High", color: DD.green, tip: `Quality score ${score}/100 — top-tier signal to Google's local algorithm.` };
  if (score >= 70) return { label: "Strong", color: DD.green, tip: `Quality score ${score}/100 — actively earning ranking signal.` };
  if (score >= 50) return { label: "Solid", color: DD.green, tip: `Quality score ${score}/100 — foundation in place, room to compound.` };
  return { label: "Building", color: DD.accent, tip: `Quality score ${score}/100 — already in play; small additions push it higher.` };
}

const LOADING_STEPS = [
  { label: "Analyzing business identity & keywords...", icon: <Search className="w-4 h-4" /> },
  { label: "Mapping services to product format...", icon: <Package className="w-4 h-4" /> },
  { label: "Writing 1000-char optimized descriptions...", icon: <PenTool className="w-4 h-4" /> },
  { label: "Building deep links & pricing strategy...", icon: <DollarSign className="w-4 h-4" /> },
  { label: "Running quality validation checks...", icon: <CheckCircle2 className="w-4 h-4" /> },
];

function QualityBar({ score }: { score: number }) {
  const color = score >= 80 ? DD.green : score >= 50 ? DD.warning : DD.danger;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: DD.border }}>
        <div className="h-full rounded-full transition-all" style={{ width: `${score}%`, background: color }} />
      </div>
      <span className="text-[11px] font-mono" style={{ color }}>{score}/100</span>
    </div>
  );
}

function CopyField({ label, value, maxLen, mono }: { label: string; value: string; maxLen?: number; mono?: boolean }) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const displayValue = value || "—";
  const isLong = displayValue.length > 200;

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="py-2.5 px-3 rounded-lg" style={{ background: DD.surface, border: `1px solid ${DD.border}` }}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: DD.textTertiary }}>{label}</span>
          {maxLen && <span className="text-[10px] font-mono" style={{ color: displayValue.length > maxLen ? DD.danger : DD.textTertiary }}>{displayValue.length}/{maxLen}</span>}
        </div>
        <Button
          size="sm"
          className="h-6 px-2.5 text-[10px]"
          style={{
            background: copied ? "rgba(34,197,94,0.2)" : "rgba(255,255,255,0.05)",
            color: copied ? DD.green : DD.textSecondary,
            border: `1px solid ${copied ? "rgba(34,197,94,0.3)" : DD.border}`,
          }}
          onClick={handleCopy}
          disabled={!value}
          data-testid={`button-copy-${label.toLowerCase().replace(/\s+/g, "-")}`}
        >
          {copied ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <ClipboardCopy className="w-3 h-3 mr-1" />}
          {copied ? "Copied ✓" : "Copy"}
        </Button>
      </div>
      <div
        className={`text-xs leading-relaxed whitespace-pre-wrap break-words ${mono ? "font-mono" : ""}`}
        style={{
          color: value ? DD.text : DD.textTertiary,
          maxHeight: isLong && !expanded ? "80px" : "none",
          overflow: isLong && !expanded ? "hidden" : "visible",
        }}
      >
        {displayValue}
      </div>
      {isLong && (
        <button
          className="text-[10px] mt-1 hover:underline"
          style={{ color: DD.accent }}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

function GbpExportCard({ product }: { product: GbpProduct }) {
  const nameParts = product.name.split(" — ");
  const gbpName = product.name.substring(0, 58);
  const priceRaw = (product.price || "").replace(/^\$/, "").trim();
  const desc = (product.description || "").substring(0, 1000);

  const handleDownloadImage = async () => {
    if (!product.imageUrl) return;
    try {
      const response = await fetch(product.imageUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ext = (product.imageUrl.split("?")[0].split(".").pop() || "jpg").toLowerCase();
      a.href = url;
      a.download = `${product.name.replace(/\s+/g, "-").toLowerCase().substring(0, 60)}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { }
  };

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ background: DD.surfaceSolid, border: `1px solid ${DD.border}` }}
      data-testid={`gbp-export-card-${product.id}`}
    >
      <div className="flex gap-4 p-4">
        <div className="shrink-0 flex flex-col items-center gap-2">
          {product.imageUrl ? (
            <a href={product.imageUrl} target="_blank" rel="noopener noreferrer">
              <img
                src={product.imageUrl}
                alt={product.name}
                className="w-[120px] h-[120px] object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                style={{ border: `1px solid ${DD.border}` }}
              />
            </a>
          ) : (
            <div
              className="w-[120px] h-[120px] rounded-lg flex items-center justify-center"
              style={{ background: DD.surface, border: `1px solid ${DD.border}` }}
            >
              <ImageIcon className="w-8 h-8" style={{ color: DD.textTertiary }} />
            </div>
          )}
          <Button
            size="sm"
            className="w-[120px] h-7 text-[10px]"
            style={{ background: DD.accent, color: "white" }}
            onClick={handleDownloadImage}
            disabled={!product.imageUrl}
            data-testid={`button-download-image-${product.id}`}
          >
            <Download className="w-3 h-3 mr-1" />
            Download Image
          </Button>
        </div>

        <div className="flex-1 space-y-2 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: DD.accent }}>GBP Fields</span>
            <div className="flex-1 h-px" style={{ background: DD.border }} />
          </div>

          <CopyField label="Product Name" value={gbpName} maxLen={58} />
          <CopyField label="Category" value={product.category || ""} />
          <CopyField label="Product Price (USD)" value={priceRaw} />
          <CopyField label="Product Description" value={desc} maxLen={1000} />
          <CopyField label="Product Landing Page URL" value={product.link || ""} maxLen={1500} />
        </div>
      </div>
    </div>
  );
}

function GbpExportView({ products, onClose }: { products: GbpProduct[]; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="gbp-export-overlay">
      <div
        className="w-[95vw] max-w-[900px] max-h-[90vh] rounded-2xl overflow-hidden flex flex-col"
        style={{ background: DD.bg, border: `1px solid ${DD.border}` }}
      >
        <div className="flex items-center justify-between px-6 py-4 shrink-0" style={{ borderBottom: `1px solid ${DD.border}` }}>
          <div>
            <h2 className="text-base font-bold text-white flex items-center gap-2" data-testid="text-gbp-export-title">
              <ClipboardCopy className="w-4 h-4" style={{ color: DD.accent }} />
              Copy for GBP — {products.length} Products
            </h2>
            <p className="text-[11px] mt-0.5" style={{ color: DD.textSecondary }}>
              Copy each field individually and paste into GBP's product form. Click images to preview full-size.
            </p>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={onClose}
            className="h-8 w-8 p-0"
            style={{ color: DD.textSecondary }}
            data-testid="button-close-gbp-export"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {products.map((product) => (
            <GbpExportCard key={product.id} product={product} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ProductCard({
  product,
  onDelete,
  onUpdate,
  onGenerateImage,
  isGeneratingImage,
  onPublish,
  isPublishing,
  canPublish,
  highlightPublish,
}: {
  product: GbpProduct;
  onDelete: () => void;
  onUpdate: (data: Partial<GbpProduct>) => void;
  onGenerateImage: () => void;
  isGeneratingImage: boolean;
  onPublish: () => void;
  isPublishing: boolean;
  canPublish: boolean;
  highlightPublish?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({
    name: product.name,
    category: product.category,
    price: product.price || "",
    description: product.description || "",
    link: product.link || "",
  });
  const { toast } = useToast();
  const priority = PRIORITY_CONFIG[product.priority || "tier2"] || PRIORITY_CONFIG.tier2;
  const status = STATUS_CONFIG[product.status || "draft"] || STATUS_CONFIG.draft;
  const charCount = (product.description || "").length;
  const charColor = charCount >= 800 ? DD.green : charCount >= 500 ? DD.warning : DD.danger;

  const handleCopy = () => {
    const text = `PRODUCT NAME: ${product.name}\nCATEGORY: ${product.category}\nPRICE: ${product.price || "N/A"}\nLINK: ${product.link || "N/A"}\n\nDESCRIPTION:\n${product.description || ""}`;
    navigator.clipboard.writeText(text);
    toast({ title: "Copied to clipboard" });
  };

  const handleCopyField = (label: string, value: string) => {
    navigator.clipboard.writeText(value);
    toast({ title: `${label} copied` });
  };

  const handleDownloadImage = async () => {
    if (!product.imageUrl) return;
    try {
      const resp = await fetch(product.imageUrl);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ext = (product.imageUrl.split("?")[0].split(".").pop() || "jpg").toLowerCase();
      a.href = url;
      a.download = `${product.name.replace(/[^a-zA-Z0-9]/g, "_")}_card.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({ title: "Image downloaded" });
    } catch {
      toast({ title: "Download failed", variant: "destructive" });
    }
  };

  const handleSave = () => {
    onUpdate(editData);
    setEditing(false);
  };

  return (
    <div
      className="rounded-xl overflow-hidden transition-all"
      style={{ background: DD.surfaceSolid, border: `1px solid ${DD.border}` }}
      data-testid={`product-card-${product.id}`}
    >
      {product.imageUrl && (
        <div className="relative w-full aspect-[16/9] overflow-hidden bg-black/30 group">
          <img
            src={product.imageUrl}
            alt={product.name}
            className="w-full h-full object-cover"
            data-testid={`img-product-${product.id}`}
          />
          <div className="absolute top-2 right-2 flex items-center gap-1.5">
            <button
              onClick={handleDownloadImage}
              className="w-7 h-7 rounded-full flex items-center justify-center backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ background: "rgba(0,0,0,0.6)", border: `1px solid rgba(255,255,255,0.15)` }}
              title="Download image"
              data-testid={`button-download-image-${product.id}`}
            >
              <Download className="w-3.5 h-3.5 text-white" />
            </button>
            <span
              className="text-[10px] px-2 py-0.5 rounded-full font-medium backdrop-blur-sm"
              style={{ color: status.color, background: "rgba(0,0,0,0.6)", border: `1px solid ${status.color}30` }}
            >
              {status.label}
            </span>
          </div>
        </div>
      )}

      <div className="p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            {editing ? (
              <Input
                value={editData.name}
                onChange={e => setEditData(d => ({ ...d, name: e.target.value }))}
                className="mb-2 text-sm"
                style={{ background: DD.bg, border: `1px solid ${DD.border}`, color: DD.text }}
                data-testid="input-product-name"
              />
            ) : (
              <div className="flex items-center gap-1.5 min-w-0">
                <h4 className="text-sm font-semibold text-white truncate" data-testid="text-product-name">{product.name}</h4>
                <button
                  onClick={() => handleCopyField("Title", product.name)}
                  className="w-8 h-8 rounded flex items-center justify-center hover:bg-white/10 transition-colors shrink-0"
                  title="Copy title"
                  data-testid={`button-copy-title-${product.id}`}
                >
                  <ClipboardCopy className="w-5 h-5" style={{ color: "#f97316" }} />
                </button>
              </div>
            )}
            <div className="flex items-center gap-2 mt-1">
              <span
                className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                style={{ color: priority.color, background: priority.bg }}
              >
                {priority.label}
              </span>
              <span className="text-[11px]" style={{ color: DD.textTertiary }}>{product.category}</span>
              {!product.imageUrl && (
                <span
                  className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                  style={{ color: status.color, background: status.bg }}
                >
                  {status.label}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {editing ? (
              <>
                <Button variant="ghost" size="icon" className="w-7 h-7" onClick={handleSave} data-testid="button-save-product">
                  <Save className="w-3.5 h-3.5 text-green-400" />
                </Button>
                <Button variant="ghost" size="icon" className="w-7 h-7" onClick={() => setEditing(false)} data-testid="button-cancel-edit">
                  <X className="w-3.5 h-3.5 text-red-400" />
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" size="icon" className="w-7 h-7" onClick={() => setEditing(true)} data-testid="button-edit-product">
                  <Edit3 className="w-3.5 h-3.5" style={{ color: DD.textSecondary }} />
                </Button>
                {product.imageUrl && (
                  <Button variant="ghost" size="icon" className="w-8 h-8" onClick={handleDownloadImage} data-testid={`button-download-card-${product.id}`} title="Download card image">
                    <Download className="w-5 h-5" style={{ color: "#f97316" }} />
                  </Button>
                )}
                <Button variant="ghost" size="icon" className="w-7 h-7" onClick={onDelete} data-testid="button-delete-product">
                  <Trash2 className="w-3.5 h-3.5" style={{ color: "#ef4444" }} />
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 mb-3">
          {editing ? (
            <Input
              value={editData.price}
              onChange={e => setEditData(d => ({ ...d, price: e.target.value }))}
              placeholder="Price"
              className="text-sm w-40"
              style={{ background: DD.bg, border: `1px solid ${DD.border}`, color: DD.text }}
              data-testid="input-product-price"
            />
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium" style={{ color: DD.green }}>{product.price || "No price set"}</span>
              {product.price && (
                <button
                  onClick={() => handleCopyField("Price", (product.price || "").replace(/[^0-9.]/g, ""))}
                  className="w-8 h-8 rounded flex items-center justify-center hover:bg-white/10 transition-colors shrink-0"
                  title="Copy price"
                  data-testid={`button-copy-price-${product.id}`}
                >
                  <ClipboardCopy className="w-5 h-5" style={{ color: "#f97316" }} />
                </button>
              )}
            </div>
          )}
          {product.priceStrategy && (
            <span className="text-[10px] px-2 py-0.5 rounded" style={{ color: DD.textTertiary, background: DD.surface }}>
              {PRICE_STRATEGIES[product.priceStrategy] || product.priceStrategy}
            </span>
          )}
        </div>

        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-wider" style={{ color: DD.textTertiary }}>Description</span>
              {!editing && product.description && (
                <button
                  onClick={() => handleCopyField("Description", product.description!)}
                  className="w-8 h-8 rounded flex items-center justify-center hover:bg-white/10 transition-colors"
                  title="Copy description"
                  data-testid={`button-copy-description-${product.id}`}
                >
                  <ClipboardCopy className="w-5 h-5" style={{ color: "#f97316" }} />
                </button>
              )}
            </div>
            <span className="text-[10px] font-mono" style={{ color: charColor }}>{charCount}/1000</span>
          </div>
          {editing ? (
            <Textarea
              value={editData.description}
              onChange={e => setEditData(d => ({ ...d, description: e.target.value }))}
              rows={6}
              className="text-xs"
              style={{ background: DD.bg, border: `1px solid ${DD.border}`, color: DD.text }}
              data-testid="input-product-description"
            />
          ) : (
            <p
              className={`text-xs leading-relaxed ${expanded ? "" : "line-clamp-3"}`}
              style={{ color: DD.textSecondary }}
              data-testid="text-product-description"
            >
              {product.description || "No description"}
            </p>
          )}
          {!editing && (product.description || "").length > 150 && (
            <button
              className="text-[11px] mt-1 flex items-center gap-1"
              style={{ color: DD.accent }}
              onClick={() => setExpanded(!expanded)}
              data-testid="button-toggle-description"
            >
              {expanded ? <><ChevronUp className="w-3 h-3" />Less</> : <><ChevronDown className="w-3 h-3" />More</>}
            </button>
          )}
        </div>

        {editing ? (
          <Input
            value={editData.link}
            onChange={e => setEditData(d => ({ ...d, link: e.target.value }))}
            placeholder="Deep link URL"
            className="text-xs mb-3"
            style={{ background: DD.bg, border: `1px solid ${DD.border}`, color: DD.text }}
            data-testid="input-product-link"
          />
        ) : product.link ? (
          <div className="flex items-center gap-1.5 mb-3">
            <Link2 className="w-3 h-3" style={{ color: DD.blue }} />
            <a
              href={product.link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] truncate hover:underline"
              style={{ color: DD.blue }}
              data-testid="link-product-url"
            >
              {product.link}
            </a>
            <ExternalLink className="w-2.5 h-2.5 shrink-0" style={{ color: DD.blue }} />
            <button
              onClick={() => handleCopyField("Link", product.link!)}
              className="w-8 h-8 rounded flex items-center justify-center hover:bg-white/10 transition-colors shrink-0"
              title="Copy link"
              data-testid={`button-copy-link-${product.id}`}
            >
              <ClipboardCopy className="w-5 h-5" style={{ color: "#f97316" }} />
            </button>
          </div>
        ) : null}

        <QualityBar score={product.qualityScore || 0} />

        {product.targetQuery && (
          <div className="mt-3 flex items-center gap-1.5">
            <Target className="w-3 h-3" style={{ color: DD.accent }} />
            <span className="text-[11px]" style={{ color: DD.textTertiary }}>Target: </span>
            <span className="text-[11px] font-medium" style={{ color: DD.text }}>{product.targetQuery}</span>
          </div>
        )}

        {product.competitorGap && (
          <div className="mt-2 px-3 py-2 rounded-lg text-[11px] leading-relaxed" style={{ background: DD.accentBg, color: DD.accent }}>
            <span className="font-semibold">Gap:</span> {product.competitorGap}
          </div>
        )}

        <div className="flex items-center gap-2 mt-3 pt-3" style={{ borderTop: `1px solid ${DD.border}` }}>
          <Button
            variant="outline"
            size="sm"
            className="flex-1 text-[11px] h-8"
            style={{ border: `1px solid ${DD.border}`, color: DD.textSecondary, background: "transparent" }}
            onClick={onGenerateImage}
            disabled={isGeneratingImage}
            data-testid={`button-gen-image-${product.id}`}
          >
            {isGeneratingImage ? (
              <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
            ) : (
              <ImageIcon className="w-3 h-3 mr-1.5" />
            )}
            {isGeneratingImage ? "AI Generating..." : product.imageUrl ? "Regen Card" : "Gen Card"}
          </Button>
          <Button
            size="sm"
            className={`flex-1 text-[11px] h-8 text-white font-semibold ${highlightPublish && product.status !== "published" ? "glow-pulse" : ""}`}
            style={{
              background: product.status === "published" ? DD.green : DD.accent,
              opacity: !canPublish || isPublishing ? 0.7 : 1,
            }}
            onClick={onPublish}
            disabled={isPublishing || !canPublish}
            title={!canPublish ? "Connect this location to GBP to publish" : product.status === "published" ? "Already on GBP — click to re-publish" : "Push this product to your Google Business Profile"}
            data-testid={`button-publish-${product.id}`}
          >
            {isPublishing ? (
              <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
            ) : product.status === "published" ? (
              <CheckCircle2 className="w-3 h-3 mr-1.5" />
            ) : (
              <UploadCloud className="w-3 h-3 mr-1.5" />
            )}
            {isPublishing ? "Publishing..." : product.status === "published" ? "Re-publish" : "Publish"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function PublishProductDialog({
  product,
  location,
  onClose,
  onMarkPublished,
  isMarking,
}: {
  product: GbpProduct | null;
  location: Location | undefined;
  onClose: () => void;
  onMarkPublished: () => void;
  isMarking: boolean;
}) {
  const { toast } = useToast();
  const [copiedField, setCopiedField] = useState<string | null>(null);

  if (!product) return null;

  const copy = (label: string, value: string) => {
    if (!navigator.clipboard?.writeText) {
      toast({
        title: "Copy not supported",
        description: "Your browser blocked clipboard access. Select the text and copy it manually.",
        variant: "destructive",
      });
      return;
    }
    navigator.clipboard.writeText(value).then(
      () => {
        setCopiedField(label);
        toast({ title: `${label} copied`, description: "Paste into the matching field on Google." });
        setTimeout(() => setCopiedField(c => c === label ? null : c), 2000);
      },
      () => {
        toast({
          title: "Could not copy",
          description: "Clipboard access was denied. Select the text and copy it manually.",
          variant: "destructive",
        });
      },
    );
  };

  // Build a Google search URL that lands the signed-in GBP owner directly
  // on the merchant panel for *their* business. We can't generate the
  // `#mpd=~{id}/editprofile/products` fragment (it requires a Google-internal
  // merchant ID + a per-session `mat=` auth token we don't have), but
  // searching by name + city + state surfaces the merchant panel inline in
  // the results with the same Products entry — works universally.
  const searchTerms = [location?.name, location?.address, location?.city, location?.state]
    .filter((s): s is string => !!s && s.trim().length > 0)
    .join(" ");
  const gbpProductsUrl = searchTerms
    ? `https://www.google.com/search?q=${encodeURIComponent(searchTerms)}`
    : "https://business.google.com/";
  // Match the card's "Copy price" behaviour — Google's Products form only
  // accepts a number, so strip the currency symbol and any prose ("Starting at
  // $199" → "199"). Display the raw value so the user still sees the formatted
  // price in the dialog, but copy the digits-only version to the clipboard.
  const priceForCopy = (product.price || "").replace(/[^0-9.]/g, "");
  const fields: { label: string; value: string | null | undefined; copyValue?: string; multiline?: boolean }[] = [
    { label: "Product name", value: product.name },
    { label: "Price", value: product.price, copyValue: priceForCopy },
    { label: "Product URL", value: product.link },
    { label: "Description", value: product.description, multiline: true },
  ];

  return (
    <Dialog open={!!product} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-3xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto" style={{ background: DD.bg, border: `1px solid ${DD.border}` }}>
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <UploadCloud className="w-5 h-5" style={{ color: DD.accent }} />
            Publish to Google Business Profile
          </DialogTitle>
          <p className="text-xs mt-2" style={{ color: DD.textSecondary }}>
            Google doesn't offer a public API for product listings, so this is a quick guided copy/paste. Takes about 60 seconds.
          </p>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-lg p-3" style={{ background: `${DD.accent}15`, border: `1px solid ${DD.accent}40` }}>
            <div className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: DD.accent }}>Step 1 — Open your Business on Google panel</div>
            <a
              href={gbpProductsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold text-white"
              style={{ background: DD.accent }}
              data-testid="link-open-gbp-products"
            >
              <ExternalLink className="w-4 h-4" />
              Open Business on Google
            </a>
            <p className="text-[11px] mt-2" style={{ color: DD.textSecondary }}>
              Sign in if needed (you'll see "Your business on Google" {location?.name ? <>for <strong style={{ color: DD.text }}>"{location.name}"</strong></> : "for the right location"}). Click the <strong style={{ color: DD.text }}>Edit products</strong> tile, then <strong style={{ color: DD.text }}>Add product</strong>.
            </p>
          </div>

          {product.imageUrl && (
            <div className="rounded-lg p-3" style={{ background: DD.surfaceSolid, border: `1px solid ${DD.border}` }}>
              <div className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: DD.blue }}>Step 2 — Save & upload the product image</div>
              <div className="flex gap-3 items-start">
                <img src={product.imageUrl} alt={product.name} className="w-28 h-28 object-cover rounded" style={{ border: `1px solid ${DD.border}` }} data-testid={`img-publish-preview-${product.id}`} />
                <div className="flex-1 space-y-2">
                  <a
                    href={product.imageUrl}
                    download={`${product.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.jpg`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-xs font-semibold text-white"
                    style={{ background: DD.blue }}
                    data-testid={`link-download-image-${product.id}`}
                  >
                    <Download className="w-3 h-3" />
                    Download image
                  </a>
                  <p className="text-[11px]" style={{ color: DD.textSecondary }}>
                    Save the image, then upload it to the product's image slot on Google.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="rounded-lg p-3" style={{ background: DD.surfaceSolid, border: `1px solid ${DD.border}` }}>
            <div className="text-[11px] font-semibold uppercase tracking-wider mb-3" style={{ color: DD.green }}>
              Step {product.imageUrl ? 3 : 2} — Copy each field into Google
            </div>
            <div className="space-y-2">
              {fields.map(({ label, value, copyValue, multiline }) => (
                <div key={label} className="rounded-md p-2.5" style={{ background: DD.bg, border: `1px solid ${DD.border}` }}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: DD.textSecondary }}>{label}</div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-[10px]"
                      onClick={() => {
                        const toCopy = copyValue ?? value;
                        if (toCopy) copy(label, toCopy);
                      }}
                      disabled={!(copyValue ?? value)}
                      style={{ color: copiedField === label ? DD.green : DD.accent }}
                      data-testid={`button-copy-${label.toLowerCase().replace(/\s+/g, "-")}-${product.id}`}
                    >
                      {copiedField === label ? (
                        <><CheckCircle2 className="w-3 h-3 mr-1" />Copied</>
                      ) : (
                        <><Copy className="w-3 h-3 mr-1" />Copy</>
                      )}
                    </Button>
                  </div>
                  {value ? (
                    <div
                      className={`text-xs ${multiline ? "whitespace-pre-wrap break-words" : "break-all"}`}
                      style={{ color: DD.text }}
                    >
                      {value}
                    </div>
                  ) : (
                    <div className="text-xs italic" style={{ color: DD.textSecondary }}>Not set</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} style={{ border: `1px solid ${DD.border}`, color: DD.textSecondary }} data-testid="button-publish-close">
            Close
          </Button>
          <Button
            onClick={onMarkPublished}
            disabled={isMarking}
            className="text-white font-semibold"
            style={{ background: DD.green }}
            data-testid={`button-mark-published-${product.id}`}
          >
            {isMarking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            {product.status === "published" ? "Already marked as published" : "I've added it to GBP"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ProductsPage() {
  const { selectedLocationId, setSelectedLocationId } = useLocationStore();
  const { toast } = useToast();
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: "", category: "", price: "", description: "", link: "" });
  const [generatingImageId, setGeneratingImageId] = useState<string | null>(null);
  const [publishingProduct, setPublishingProduct] = useState<GbpProduct | null>(null);
  const [showManualPopup, setShowManualPopup] = useState(false);
  const [glowFirstPublish, setGlowFirstPublish] = useState(false);

  const { data: locations } = useQuery<Location[]>({ queryKey: ["/api/locations"] });

  const locationId = selectedLocationId || locations?.[0]?.id || "";

  useEffect(() => {
    if (!selectedLocationId && locations && locations.length > 0) {
      setSelectedLocationId(locations[0].id);
    }
  }, [selectedLocationId, locations, setSelectedLocationId]);

  const location = locations?.find(l => l.id === locationId);

  useEffect(() => {
    if (!locationId || !location?.gbpLocationId) return;
    fetch(`/api/locations/${locationId}/gbp-product-baseline`, { method: "POST", credentials: "include" }).catch(() => {});
  }, [locationId, location?.gbpLocationId]);

  const { data: products = [], isLoading } = useQuery<GbpProduct[]>({
    queryKey: ["/api/products", locationId],
    enabled: !!locationId,
  });

  // First time the Product Forge page is available with products to publish,
  // surface a one-time popup explaining products must be uploaded manually to
  // GBP, then glow/pulse the first product's Publish button to guide the user.
  const MANUAL_UPLOAD_ACK_KEY = "pf_manual_upload_ack_v1";
  useEffect(() => {
    if (isLoading || products.length === 0) return;
    try {
      if (localStorage.getItem(MANUAL_UPLOAD_ACK_KEY)) return;
    } catch { /* localStorage unavailable */ }
    setShowManualPopup(true);
  }, [isLoading, products.length]);

  const acknowledgeManualUpload = () => {
    try { localStorage.setItem(MANUAL_UPLOAD_ACK_KEY, "1"); } catch { /* ignore */ }
    setShowManualPopup(false);
    setGlowFirstPublish(true);
  };

  const generateMutation = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/products/generate/${locationId}`),
    onSuccess: async (res) => {
      const data = await res.json();
      queryClient.invalidateQueries({ queryKey: ["/api/products", locationId] });
      queryClient.invalidateQueries({ queryKey: ["/api/locations"] });
      toast({ title: `${data.count} products forged`, description: "Review and edit your product listings below." });
      setGenerateModalOpen(false);
    },
    onError: (err: Error) => {
      toast({ title: "Generation failed", description: err.message, variant: "destructive" });
      setGenerateModalOpen(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/products/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/products", locationId] });
      toast({ title: "Product deleted" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<GbpProduct> }) =>
      apiRequest("PATCH", `/api/products/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/products", locationId] });
      toast({ title: "Product updated" });
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => apiRequest("POST", "/api/products", { locationId, ...newProduct }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/products", locationId] });
      toast({ title: "Product created" });
      setAddDialogOpen(false);
      setNewProduct({ name: "", category: "", price: "", description: "", link: "" });
    },
  });

  const regenCardsMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/products/regenerate-cards/${locationId}`);
      return res.json();
    },
    onSuccess: (data) => {
      toast({ title: `Regenerating ${data.count} product cards`, description: "Cards will update in the background. Refresh in a moment to see them." });
      queryClient.invalidateQueries({ queryKey: ["/api/locations"] });
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["/api/products", locationId] });
        queryClient.invalidateQueries({ queryKey: ["/api/locations"] });
      }, 15000);
    },
    onError: (err: Error) => {
      toast({ title: "Regeneration failed", description: err.message, variant: "destructive" });
    },
  });

  const generateImageMutation = useMutation({
    mutationFn: async (productId: string) => {
      setGeneratingImageId(productId);
      const res = await apiRequest("POST", `/api/products/${productId}/generate-image`);
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/products", locationId] });
      if (data.imageUrl) {
        toast({ title: "Image generated", description: "Product image has been created and saved." });
      } else {
        toast({ title: "Image unavailable", description: data.message || "Could not generate image.", variant: "destructive" });
      }
      setGeneratingImageId(null);
      generateImageMutation.reset();
    },
    onError: (err: Error) => {
      toast({ title: "Image generation failed", description: err.message, variant: "destructive" });
      setGeneratingImageId(null);
      generateImageMutation.reset();
    },
  });

  const markPublishedMutation = useMutation({
    mutationFn: async (productId: string) => {
      const res = await apiRequest("POST", `/api/products/${productId}/mark-published`);
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/products", locationId] });
      // Also refresh road-to-domination progress so the next stage unlocks
      // immediately — publish_product is a prereq for /social and /citations.
      queryClient.invalidateQueries({ queryKey: ["/api/locations", locationId, "rtd-progress"] });
      if (data.alreadyPublished) {
        toast({ title: "Already marked as published" });
      } else {
        toast({ title: "Marked as published", description: "Nice work! +5 tokens awarded. Next stage unlocked." });
      }
      setPublishingProduct(null);
    },
    onError: (err: Error) => {
      toast({ title: "Could not update status", description: err.message, variant: "destructive" });
    },
  });

  const handleGenerate = () => {
    setGenerateModalOpen(true);
    setLoadingStep(0);
    generateMutation.mutate();
    const timers: ReturnType<typeof setTimeout>[] = [];
    [3000, 6000, 12000, 16000].forEach((ms, i) => {
      timers.push(setTimeout(() => setLoadingStep(i + 1), ms));
    });
  };

  const tier1 = products.filter(p => p.priority === "tier1");
  const tier2 = products.filter(p => p.priority === "tier2");
  const tier3 = products.filter(p => p.priority === "tier3");
  const avgChars = products.length ? Math.round(products.reduce((s, p) => s + (p.charCount || 0), 0) / products.length) : 0;
  const avgScore = products.length ? Math.round(products.reduce((s, p) => s + (p.qualityScore || 0), 0) / products.length) : 0;
  const underCharProducts = products.filter(p => (p.charCount || 0) < 800);
  const categories = [...new Set(products.map(p => p.category))];
  const publishedCount = products.filter(p => p.status === "published").length;
  // Rendered order is tier1 → tier2 → tier3; the first not-yet-published product
  // is the one whose Publish button we glow after the manual-upload popup.
  const firstPublishId = ([...tier1, ...tier2, ...tier3].find(p => p.status !== "published")?.id) ?? null;

  return (
    <>
    <div className="p-4 sm:p-6 pb-24 space-y-5 overflow-y-auto h-full" style={{ background: DD.bg, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {generateModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="generate-modal-overlay">
          <div className="w-[95vw] max-w-[480px] rounded-2xl overflow-hidden" style={{ background: DD.bg, border: `1px solid ${DD.border}` }}>
            <div className="px-6 pt-6 pb-2 text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-2xl flex items-center justify-center" style={{ background: DD.accentBg, border: `1px solid ${DD.accentBorder}` }}>
                <Package className="w-7 h-7 animate-pulse" style={{ color: DD.accent }} />
              </div>
              <h3 className="text-lg font-bold text-white" data-testid="text-forge-title">Forging Products</h3>
              <p className="text-xs text-white/40 mt-1">AI is building optimized product listings from your business data</p>
            </div>
            <div className="p-6 space-y-3">
              {LOADING_STEPS.map((step, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-500 ${i <= loadingStep ? "opacity-100 translate-x-0" : "opacity-30 translate-x-4"}`}
                  style={{ background: i === loadingStep ? DD.accentBg : DD.surface, border: `1px solid ${i === loadingStep ? DD.accentBorder : DD.border}` }}
                >
                  <span style={{ color: i <= loadingStep ? DD.accent : DD.textTertiary }}>{step.icon}</span>
                  <span className="text-sm font-medium flex-1" style={{ color: i <= loadingStep ? DD.text : DD.textTertiary }}>{step.label}</span>
                  {i < loadingStep && <CheckCircle2 className="w-4 h-4 text-green-400" />}
                  {i === loadingStep && <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: DD.accent }} />}
                </div>
              ))}
            </div>
            <div className="px-6 pb-5 pt-1 text-center">
              <p className="text-[11px] text-white/30">Products will appear as drafts for your review</p>
            </div>
          </div>
        </div>
      )}

      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent style={{ background: DD.bg, border: `1px solid ${DD.border}` }}>
          <DialogHeader>
            <DialogTitle className="text-white">Add Product Manually</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Product name" value={newProduct.name} onChange={e => setNewProduct(d => ({ ...d, name: e.target.value }))} style={{ background: DD.surfaceSolid, border: `1px solid ${DD.border}`, color: DD.text }} data-testid="input-new-product-name" />
            <Input placeholder="Category" value={newProduct.category} onChange={e => setNewProduct(d => ({ ...d, category: e.target.value }))} style={{ background: DD.surfaceSolid, border: `1px solid ${DD.border}`, color: DD.text }} data-testid="input-new-product-category" />
            <Input placeholder="Price (e.g. Starting at $499/mo)" value={newProduct.price} onChange={e => setNewProduct(d => ({ ...d, price: e.target.value }))} style={{ background: DD.surfaceSolid, border: `1px solid ${DD.border}`, color: DD.text }} data-testid="input-new-product-price" />
            <Input placeholder="Deep link URL" value={newProduct.link} onChange={e => setNewProduct(d => ({ ...d, link: e.target.value }))} style={{ background: DD.surfaceSolid, border: `1px solid ${DD.border}`, color: DD.text }} data-testid="input-new-product-link" />
            <Textarea placeholder="Description (aim for 800-1000 characters)" value={newProduct.description} onChange={e => setNewProduct(d => ({ ...d, description: e.target.value }))} rows={6} style={{ background: DD.surfaceSolid, border: `1px solid ${DD.border}`, color: DD.text }} data-testid="input-new-product-description" />
            <div className="text-right text-[11px] font-mono" style={{ color: newProduct.description.length >= 800 ? DD.green : DD.warning }}>{newProduct.description.length}/1000</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)} style={{ border: `1px solid ${DD.border}`, color: DD.textSecondary }}>Cancel</Button>
            <Button onClick={() => createMutation.mutate()} disabled={!newProduct.name || !newProduct.category || createMutation.isPending} style={{ background: DD.accent }} className="text-white" data-testid="button-save-new-product">
              {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Product
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PublishProductDialog
        product={publishingProduct}
        location={location}
        onClose={() => setPublishingProduct(null)}
        onMarkPublished={() => publishingProduct && markPublishedMutation.mutate(publishingProduct.id)}
        isMarking={markPublishedMutation.isPending}
      />

      <Dialog open={showManualPopup} onOpenChange={(open) => { if (!open) acknowledgeManualUpload(); }}>
        <DialogContent
          className="max-w-2xl p-8 gap-6"
          style={{ background: DD.surfaceSolid, border: `1px solid ${DD.border}`, color: DD.text }}
          data-testid="dialog-manual-upload"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-white text-3xl">
              <UploadCloud className="w-9 h-9" style={{ color: DD.accent }} />
              Products are uploaded manually
            </DialogTitle>
          </DialogHeader>
          <p className="text-[1.75rem] leading-relaxed" style={{ color: DD.textSecondary }}>
            Google doesn't allow products to be published automatically, so each one has to be
            added to your Google Business Profile by hand. We've done the hard part — your
            listings are fully optimized and ready. Just hit <span className="font-semibold" style={{ color: DD.text }}>Publish</span> on
            a product and we'll walk you through copying it into GBP, field by field.
          </p>
          <DialogFooter>
            <Button
              className="text-white font-semibold w-full h-14 text-xl"
              style={{ background: DD.accent }}
              onClick={acknowledgeManualUpload}
              data-testid="button-understand-manual-upload"
            >
              I understand
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2" data-testid="text-page-title">
            <Package className="w-5 h-5" style={{ color: DD.accent }} />
            Product Forge
          </h1>
          <p className="text-xs mt-1" style={{ color: DD.textSecondary }}>
            AI-powered GBP product listings optimized for AI Overviews & rich snippets
          </p>
        </div>
        <div className="flex items-center gap-2">
          <RtdOptOut
            locationId={locationId}
            taskIds={["products", "publish_product"]}
            stageName="Products"
            warningTitle="Skip Product Forge for now?"
            warningBody={
              <p>
                Heads up — GBP products show up directly in your listing and in Google search results,
                giving customers an immediate view of what you offer. Filling out this section sends a
                strong relevance signal to Google about what your business does and helps you rank for
                more service-related searches. Skipping this step usually means slower ranking growth.
              </p>
            }
          />
          {products.length > 0 && (
            <>
              <Button
                variant="outline"
                className="text-xs"
                style={{ border: `1px solid ${DD.purple || "#a855f7"}40`, color: DD.purple || "#a855f7", background: "rgba(168,85,247,0.05)" }}
                onClick={() => {
                  const a = document.createElement("a");
                  a.href = `/api/products/export-agent/${locationId}`;
                  a.download = "gbp-products-export.json";
                  a.click();
                }}
                data-testid="button-export-agent"
              >
                <Bot className="w-4 h-4 mr-2" />Export for Agent
              </Button>
              <Button
                variant="outline"
                className="text-xs"
                style={{ border: `1px solid ${DD.blue}40`, color: DD.blue, background: "rgba(59,130,246,0.05)" }}
                onClick={() => regenCardsMutation.mutate()}
                disabled={regenCardsMutation.isPending}
                data-testid="button-regen-cards"
              >
                <ClipboardCopy className="w-4 h-4 mr-2" />{regenCardsMutation.isPending ? "Regenerating..." : "Regen Product Cards"}
              </Button>
            </>
          )}
          <Button
            variant="outline"
            className="text-xs"
            style={{ border: `1px solid ${DD.border}`, color: DD.textSecondary, background: "transparent" }}
            onClick={handleGenerate}
            disabled={!locationId || generateMutation.isPending}
            data-testid="button-forge-products"
          >
            <Sparkles className="w-4 h-4 mr-2" />{generateMutation.isPending ? "Forging..." : "Forge Products"}
          </Button>
          <Button
            className="text-white font-semibold text-xs"
            style={{ background: DD.accent }}
            onClick={() => setAddDialogOpen(true)}
            data-testid="button-add-product"
          >
            <Plus className="w-4 h-4 mr-1" />Add Product
          </Button>
        </div>
      </div>

      {products.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-3" data-testid="grid-portfolio-stats">
          {[
            {
              label: "PRODUCTS LIVE",
              value: products.length,
              color: DD.green,
              tip: `${products.length} optimized service pages in your portfolio.`,
            },
            {
              label: "PUSHED TO GBP",
              value: publishedCount,
              color: DD.green,
              tip: `${publishedCount} of ${products.length} pushed to your live GBP product feed.`,
            },
            {
              label: "CATEGORIES COVERED",
              value: categories.length,
              color: DD.green,
              tip: `${categories.length} service categories — broad topical coverage signals authority to Google.`,
            },
            {
              label: "AVG DEPTH",
              value: depthLabel(avgChars).label,
              color: depthLabel(avgChars).color,
              tip: `Average ${avgChars} characters per product — ${depthLabel(avgChars).tip}`,
            },
            {
              label: "AVG SIGNAL",
              value: signalLabel(avgScore).label,
              color: signalLabel(avgScore).color,
              tip: `Average quality score ${avgScore}/100 — ${signalLabel(avgScore).tip}`,
            },
            {
              label: "ROOM TO AMPLIFY",
              value: underCharProducts.length === 0 ? "All set" : underCharProducts.length,
              color: underCharProducts.length === 0 ? DD.green : DD.accent,
              tip: underCharProducts.length === 0
                ? "Every product has strong depth — fully amplified."
                : `${underCharProducts.length} product${underCharProducts.length === 1 ? "" : "s"} with room to push depth higher.`,
            },
          ].map((stat, i) => (
            <div
              key={i}
              className="rounded-xl px-4 py-3 text-center cursor-help"
              style={{ background: DD.surface, border: `1px solid ${DD.border}` }}
              title={stat.tip}
              data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="text-lg font-bold" style={{ color: stat.color }}>{stat.value}</div>
              <div className="text-[10px] uppercase tracking-wider mt-0.5" style={{ color: DD.textTertiary }}>{stat.label}</div>
            </div>
          ))}
        </div>
      )}

      {!locationId ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Package className="w-12 h-12 mb-4" style={{ color: DD.textTertiary }} />
          <h3 className="text-base font-semibold text-white mb-2">Select a location</h3>
          <p className="text-xs" style={{ color: DD.textSecondary }}>Choose a location from the sidebar to manage its products.</p>
        </div>
      ) : isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: DD.accent }} />
        </div>
      ) : products.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-5" style={{ background: DD.accentBg, border: `1px solid ${DD.accentBorder}` }}>
            <Package className="w-10 h-10" style={{ color: DD.accent }} />
          </div>
          <h3 className="text-base font-semibold text-white mb-2" data-testid="text-empty-title">No products yet</h3>
          <p className="text-xs max-w-sm mb-5" style={{ color: DD.textSecondary }}>
            The Products tab on your GBP renders rich snippets with images, descriptions, pricing, and clickable links — 
            dramatically richer than the plain-text Services tab. Forge AI-optimized products to dominate AI Overviews.
          </p>
          <Button
            onClick={handleGenerate}
            disabled={generateMutation.isPending}
            className="text-white font-semibold"
            style={{ background: DD.accent }}
            data-testid="button-forge-empty"
          >
            <Sparkles className="w-4 h-4 mr-2" />Forge Products with AI
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {[
            { title: "Tier 1 — Pillar Services", items: tier1, desc: "Highest revenue services with dedicated website pages" },
            { title: "Tier 2 — Secondary Services", items: tier2, desc: "Secondary services with moderate search volume" },
            { title: "Tier 3 — Niche / Seasonal", items: tier3, desc: "Niche or seasonal services" },
          ].filter(g => g.items.length > 0).map(group => (
            <div key={group.title}>
              <div className="flex items-center gap-3 mb-3">
                <h3 className="text-sm font-semibold text-white">{group.title}</h3>
                <span className="text-[10px]" style={{ color: DD.textTertiary }}>{group.desc}</span>
                <Badge variant="outline" className="text-[10px]" style={{ borderColor: DD.border, color: DD.textSecondary }}>
                  {group.items.length}
                </Badge>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {group.items.map(product => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onDelete={() => deleteMutation.mutate(product.id)}
                    onUpdate={(data) => updateMutation.mutate({ id: product.id, data })}
                    onGenerateImage={() => generateImageMutation.mutate(product.id)}
                    isGeneratingImage={generatingImageId === product.id}
                    onPublish={() => { setPublishingProduct(product); setGlowFirstPublish(false); }}
                    isPublishing={false}
                    canPublish={true}
                    highlightPublish={glowFirstPublish && product.id === firstPublishId}
                  />
                ))}
              </div>
            </div>
          ))}

          {categories.length > 0 && (
            <div className="rounded-xl overflow-hidden" style={{ background: DD.surfaceSolid, border: `1px solid ${DD.border}` }}>
              <div className="px-4 py-3 border-b" style={{ borderColor: DD.border }}>
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <FileText className="w-4 h-4" style={{ color: DD.accent }} />
                  Portfolio Summary
                </h3>
              </div>

              {/* Signals Sent celebration band — leads with what's already winning. */}
              <div
                className="px-4 py-3 border-b grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px]"
                style={{ borderColor: DD.border, background: "rgba(34,197,94,0.04)" }}
                data-testid="band-signals-sent"
              >
                <div className="flex items-center gap-2" data-testid="signal-pages-live">
                  <CheckCircle2 className="w-3.5 h-3.5 shrink-0" style={{ color: DD.green }} />
                  <span style={{ color: DD.text }}>
                    <span className="font-semibold">{products.length} service page{products.length === 1 ? "" : "s"} live</span>
                    <span style={{ color: DD.textSecondary }}> · sending {products.length} ranking signal{products.length === 1 ? "" : "s"} to Google</span>
                  </span>
                </div>
                <div className="flex items-center gap-2" data-testid="signal-targeting">
                  <CheckCircle2 className="w-3.5 h-3.5 shrink-0" style={{ color: DD.green }} />
                  <span style={{ color: DD.text }}>
                    <span className="font-semibold">{Math.round((products.filter(p => p.category).length / Math.max(1, products.length)) * 100)}%</span>
                    <span style={{ color: DD.textSecondary }}> have category targeting · </span>
                    <span className="font-semibold">{Math.round((products.filter(p => p.imageUrl).length / Math.max(1, products.length)) * 100)}%</span>
                    <span style={{ color: DD.textSecondary }}> have visuals</span>
                  </span>
                </div>
                <div className="flex items-center gap-2" data-testid="signal-depth">
                  <CheckCircle2 className="w-3.5 h-3.5 shrink-0" style={{ color: DD.green }} />
                  <span style={{ color: DD.text }}>
                    <span className="font-semibold">Avg depth: {depthLabel(avgChars).label}</span>
                    <span style={{ color: DD.textSecondary }}> ({avgChars} chars/page)</span>
                  </span>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-xs">
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${DD.border}` }}>
                      <th className="text-left px-4 py-2 font-medium" style={{ color: DD.textTertiary }}>#</th>
                      <th className="text-left px-4 py-2 font-medium" style={{ color: DD.textTertiary }}>Product Name</th>
                      <th className="text-left px-4 py-2 font-medium" style={{ color: DD.textTertiary }}>Category</th>
                      <th className="text-left px-4 py-2 font-medium" style={{ color: DD.textTertiary }}>Price</th>
                      <th
                        className="text-left px-4 py-2 font-medium cursor-help"
                        style={{ color: DD.textTertiary }}
                        title="How comprehensive each product description is — Google rewards depth as a signal of topical authority."
                      >
                        Depth
                      </th>
                      <th
                        className="text-left px-4 py-2 font-medium cursor-help"
                        style={{ color: DD.textTertiary }}
                        title="Overall quality signal each product is sending to Google's local algorithm."
                      >
                        Signal
                      </th>
                      <th
                        className="text-left px-4 py-2 font-medium cursor-help"
                        style={{ color: DD.textTertiary }}
                        title="Geo-tagged product imagery reinforces your service area to Google."
                      >
                        Visuals
                      </th>
                      <th
                        className="text-left px-4 py-2 font-medium cursor-help"
                        style={{ color: DD.textTertiary }}
                        title="Whether each product is live on your GBP feed and being crawled."
                      >
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((p, i) => {
                      const st = STATUS_CONFIG[p.status || "draft"] || STATUS_CONFIG.draft;
                      const depth = depthLabel(p.charCount || 0);
                      const signal = signalLabel(p.qualityScore || 0);
                      return (
                        <tr key={p.id} style={{ borderBottom: `1px solid ${DD.border}` }}>
                          <td className="px-4 py-2" style={{ color: DD.textTertiary }}>{i + 1}</td>
                          <td className="px-4 py-2 font-medium text-white max-w-[250px] truncate">{p.name}</td>
                          <td className="px-4 py-2" style={{ color: DD.textSecondary }}>{p.category}</td>
                          <td className="px-4 py-2" style={{ color: DD.green }}>{p.price || "—"}</td>
                          <td className="px-4 py-2">
                            <span
                              className="cursor-help"
                              style={{ color: depth.color, fontWeight: 500 }}
                              title={depth.tip}
                              data-testid={`cell-depth-${p.id}`}
                            >
                              {depth.label}
                            </span>
                          </td>
                          <td className="px-4 py-2">
                            <span
                              className="cursor-help"
                              style={{ color: signal.color, fontWeight: 500 }}
                              title={signal.tip}
                              data-testid={`cell-signal-${p.id}`}
                            >
                              {signal.label}
                            </span>
                          </td>
                          <td className="px-4 py-2">
                            {p.imageUrl ? (
                              <span
                                className="inline-flex items-center gap-1 cursor-help"
                                style={{ color: DD.green }}
                                title="Geo-tagged visual attached — reinforces your service area to Google's local algorithm."
                                data-testid={`cell-visuals-${p.id}`}
                              >
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                <span className="text-[11px]">Geo-tagged</span>
                              </span>
                            ) : (
                              <span
                                className="inline-flex items-center gap-1 cursor-help"
                                style={{ color: DD.accent }}
                                title="Adding a visual unlocks an extra ranking signal for this product."
                                data-testid={`cell-visuals-${p.id}`}
                              >
                                <Plus className="w-3.5 h-3.5" />
                                <span className="text-[11px]">Add visual</span>
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2">
                            <span
                              className="text-[10px] px-2 py-0.5 rounded-full cursor-help"
                              style={{ color: st.color, background: st.bg }}
                              title={st.tip}
                              data-testid={`cell-status-${p.id}`}
                            >
                              {st.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
    </>
  );
}
